<template>
  <div id="patchmanager">
    <vue-loading :active.sync="loading" :is-full-page="true" loader="dots" color="#70A1F0"></vue-loading>

    <nav class="navbar fixed-top-sub navbar-light bg-light" v-if="active != null">
      <div class="navbar-brand">Patch Manager</div>
      <ul class="nav nav-pages mr-auto">
        <li class="nav-item">
          <a class="nav-link" :class="{'active': view === 'overview'}" type="button" @click="view='overview'">Overview</a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{'active': view === 'critical'}" type="button" @click="view='critical'">Critical / Important Patches</a>
        </li>
      </ul>

      <div class="nav-item btn-group dropdown">
        <button type="button" class="btn-block btn dropdown-toggle btn-secondary text-right" style="margin-right: 10px;" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          {{active.account_name || active.account}}
        </button>
        <div class="dropdown-menu dropdown-menu-right scrollable-menu">
          <h6 class="dropdown-header">Accounts</h6>
          <div v-for="report in patchmanager" :key="report.account">
            <a class="dropdown-item" @click="setActive(report)">{{ report.account_name || report.account }}</a>
          </div>
        </div>
      </div>
      <button type="button" class="btn btn-primary text-right" @click="downloadCSV">Download Report</button>
    </nav>

    <!-- Overview Tab -->
    <div v-if="view === 'overview' && active != null">
      <div class="row">
        <div class="col-3">
          <div class="row">
            <transition name="fade" appear>
              <div class="card col">
                <div class="card-heading">
                  <h4>Account</h4>
                </div>
                <div class="card-body">
                  <dl>
                    <dt>Contract</dt><dd>{{active.contract}}</dd>
                    <dt>Account Name</dt><dd>{{active.account_name}}</dd>
                    <dt>Account Id</dt><dd>{{active.account}}</dd>
                    <dt>Total Instances</dt><dd>{{activeCompliance.length}}</dd>
                    <dt>Last Updated</dt><dd>{{formatTimestamp(active.timestamp)}}</dd>
                  </dl>
                </div>
              </div>
            </transition>
          </div>

          <div class="row">
            <transition name="fade" appear>
              <div class="card col">
                <div class="card-heading">
                  <h4>Compliance Summary</h4>
                </div>
                <div class="card-body">
                  <div class="compliance-stat">
                    <span class="stat-label compliant">Compliant</span>
                    <span class="stat-count">{{complianceCounts.compliant}}</span>
                  </div>
                  <div class="compliance-stat">
                    <span class="stat-label non-compliant">Non-Compliant</span>
                    <span class="stat-count">{{complianceCounts.nonCompliant}}</span>
                  </div>
                  <div class="compliance-stat">
                    <span class="stat-label compliant-pct">Compliance Rate</span>
                    <span class="stat-count">{{complianceRate}}%</span>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <div class="row">
            <transition name="fade" appear>
              <div class="card col">
                <div class="card-heading">
                  <h4>Platform Breakdown</h4>
                </div>
                <div class="card-body">
                  <div class="severity-bar" v-for="(count, platform) in platformCounts" :key="platform">
                    <span class="platform-label">{{platform || 'Unknown'}}</span>
                    <span class="severity-count">{{count}}</span>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div class="col-9">
          <transition name="fade" appear>
            <vue-good-table
              :columns="complianceColumns"
              :rows="activeCompliance"
              :search-options="{
                enabled: true,
                skipDiacritics: true,
                placeholder: 'filter instances',
              }"
              :pagination-options="{
                enabled: true,
                perPage: 25,
              }"
              :sort-options="{
                enabled: true,
                initialSortBy: {field: 'missing_count', type: 'desc'}
              }"
              :row-style-class="complianceRowStyleFn"
              styleClass="vgt-table condensed">
            </vue-good-table>
          </transition>
        </div>
      </div>
    </div>

    <!-- Critical Patches Tab -->
    <div v-if="view === 'critical' && active != null">
      <div class="row">
        <div class="col-3">
          <div class="row">
            <transition name="fade" appear>
              <div class="card col">
                <div class="card-heading">
                  <h4>Account</h4>
                </div>
                <div class="card-body">
                  <dl>
                    <dt>Contract</dt><dd>{{active.contract}}</dd>
                    <dt>Account Name</dt><dd>{{active.account_name}}</dd>
                    <dt>Account Id</dt><dd>{{active.account}}</dd>
                    <dt>Critical / Important</dt><dd>{{activeCriticalPatches.length}}</dd>
                  </dl>
                </div>
              </div>
            </transition>
          </div>

          <div class="row">
            <transition name="fade" appear>
              <div class="card col">
                <div class="card-heading">
                  <h4>Patch State Breakdown</h4>
                </div>
                <div class="card-body">
                  <div class="severity-bar" v-for="state in patchStateOrder" :key="state">
                    <span class="severity-label" :class="'state-' + state.toLowerCase()">{{state}}</span>
                    <span class="severity-count">{{patchStateCounts[state] || 0}}</span>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <div class="row" v-if="activePatch != null">
            <transition name="fade" appear>
              <div class="card col">
                <div class="card-heading">
                  <h4>Patch Detail</h4>
                </div>
                <div class="card-body finding-detail">
                  <dl>
                    <dt>KB / Patch ID</dt>
                    <dd>{{activePatch.kb_id}}</dd>
                    <dt>Title</dt>
                    <dd>{{activePatch.title}}</dd>
                    <dt>Classification</dt>
                    <dd>{{activePatch.classification}}</dd>
                    <dt>Severity</dt>
                    <dd><span :class="'severity-badge severity-' + activePatch.severity.toLowerCase()">{{activePatch.severity}}</span></dd>
                    <dt>State</dt>
                    <dd><span :class="'state-badge state-' + activePatch.state.toLowerCase()">{{activePatch.state}}</span></dd>
                    <dt>Instance</dt>
                    <dd>{{activePatch.instance_id}}</dd>
                    <dt>Instance Name</dt>
                    <dd>{{activePatch.instance_name}}</dd>
                    <dt>Platform</dt>
                    <dd>{{activePatch.platform_name}}</dd>
                    <dt>Region</dt>
                    <dd>{{activePatch.region}}</dd>
                    <dt v-if="activePatch.installed_time">Installed</dt>
                    <dd v-if="activePatch.installed_time">{{activePatch.installed_time}}</dd>
                  </dl>
                  <div v-if="activePatch.cve_ids && activePatch.cve_ids.length" class="detail-section">
                    <h5>CVE IDs</h5>
                    <div v-for="cve in activePatch.cve_ids" :key="cve" class="cve-item">
                      <a :href="'https://nvd.nist.gov/vuln/detail/' + cve" target="_blank">{{cve}}</a>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div class="col-9">
          <transition name="fade" appear>
            <vue-good-table
              :columns="criticalPatchColumns"
              :rows="activeCriticalPatches"
              @on-row-click="onPatchRowClick"
              :search-options="{
                enabled: true,
                skipDiacritics: true,
                placeholder: 'filter patches',
              }"
              :pagination-options="{
                enabled: true,
                perPage: 25,
              }"
              :sort-options="{
                enabled: true,
                initialSortBy: {field: 'state', type: 'asc'}
              }"
              :row-style-class="patchRowStyleFn"
              styleClass="vgt-table condensed">
              <div slot="table-actions">
                <button type="button" class="btn btn-danger btn-margin btn-sm" @click="filterByState('Missing')">Missing</button>
                <button type="button" class="btn btn-warning btn-margin btn-sm" @click="filterByState('Failed')">Failed</button>
                <button type="button" class="btn btn-success btn-margin btn-sm" @click="filterByState('Installed')">Installed</button>
                <button type="button" class="btn btn-secondary btn-margin btn-sm" @click="showAllPatches">Show All</button>
              </div>
            </vue-good-table>
          </transition>
        </div>
      </div>
    </div>

    <div class="row empty-state" v-if="!loading && patchmanager.length === 0">
      <div class="col text-center">
        <h4>No Patch Manager data available</h4>
        <p>Patch compliance data will appear here once the feature is deployed and reports are collected.</p>
      </div>
    </div>
  </div>
</template>

<script>
import HTTP from '@/common/http'
import VueLoading from 'vue-loading-overlay'
import { mapGetters } from 'vuex'

export default {
  name: 'PatchManager',
  props: [
    'id'
  ],
  components: {
    VueLoading
  },
  computed: {
    ...mapGetters([
      'activeType'
    ]),
    activeCompliance () {
      if (!this.active || !this.active.compliance_summary) return []
      return this.active.compliance_summary
    },
    activeCriticalPatches () {
      if (!this.active || !this.active.critical_patches) return []
      let patches = this.active.critical_patches.map(p => {
        const cves = p.cve_ids || []
        const cveDisplay = Array.isArray(cves) ? cves.join(', ') : String(cves)
        return { ...p, cveDisplay }
      })
      if (this.stateFilter) {
        patches = patches.filter(p => p.state === this.stateFilter)
      }
      return patches
    },
    complianceCounts () {
      const summary = this.activeCompliance
      const compliant = summary.filter(s => s.compliance_status === 'COMPLIANT').length
      const nonCompliant = summary.filter(s => s.compliance_status === 'NON_COMPLIANT').length
      return { compliant, nonCompliant }
    },
    complianceRate () {
      const total = this.activeCompliance.length
      if (total === 0) return 0
      return Math.round((this.complianceCounts.compliant / total) * 100)
    },
    platformCounts () {
      const counts = {}
      for (const inst of this.activeCompliance) {
        const platform = inst.platform_name || 'Unknown'
        counts[platform] = (counts[platform] || 0) + 1
      }
      return counts
    },
    patchStateCounts () {
      if (!this.active || !this.active.critical_patches) return {}
      const counts = {}
      for (const p of this.active.critical_patches) {
        counts[p.state] = (counts[p.state] || 0) + 1
      }
      return counts
    }
  },
  data () {
    return {
      loading: false,
      patchmanager: [],
      active: null,
      activePatch: null,
      selectedRow: -1,
      view: 'overview',
      stateFilter: null,
      patchStateOrder: ['Missing', 'Failed', 'Installed', 'InstalledOther', 'InstalledPendingReboot', 'InstalledRejected', 'NotApplicable'],
      complianceColumns: [
        {
          label: 'Instance ID',
          field: 'instance_id',
          width: '140px'
        },
        {
          label: 'Instance Name',
          field: 'instance_name'
        },
        {
          label: 'Computer Name',
          field: 'computer_name'
        },
        {
          label: 'Platform',
          field: 'platform_name',
          filterOptions: {
            enabled: true,
            placeholder: 'All'
          }
        },
        {
          label: 'Status',
          field: 'compliance_status',
          width: '130px',
          filterOptions: {
            enabled: true,
            placeholder: 'All'
          }
        },
        {
          label: 'Installed',
          field: 'installed_count',
          type: 'number',
          width: '85px'
        },
        {
          label: 'Missing',
          field: 'missing_count',
          type: 'number',
          width: '80px'
        },
        {
          label: 'Failed',
          field: 'failed_count',
          type: 'number',
          width: '70px'
        },
        {
          label: 'Pending Reboot',
          field: 'installed_pending_reboot_count',
          type: 'number',
          width: '110px'
        },
        {
          label: 'Critical NC',
          field: 'critical_non_compliant_count',
          type: 'number',
          width: '90px'
        },
        {
          label: 'Security NC',
          field: 'security_non_compliant_count',
          type: 'number',
          width: '95px'
        },
        {
          label: 'Region',
          field: 'region',
          width: '140px',
          filterOptions: {
            enabled: true,
            placeholder: 'All'
          }
        },
        {
          label: 'Patch Group',
          field: 'patch_group',
          width: '120px'
        }
      ],
      criticalPatchColumns: [
        {
          label: 'KB / Patch ID',
          field: 'kb_id',
          width: '180px'
        },
        {
          label: 'Title',
          field: 'title'
        },
        {
          label: 'Severity',
          field: 'severity',
          width: '100px',
          filterOptions: {
            enabled: true,
            placeholder: 'All'
          }
        },
        {
          label: 'Instance',
          field: 'instance_id',
          width: '140px'
        },
        {
          label: 'Instance Name',
          field: 'instance_name',
          width: '140px'
        },
        {
          label: 'Platform',
          field: 'platform_name',
          width: '140px',
          filterOptions: {
            enabled: true,
            placeholder: 'All'
          }
        },
        {
          label: 'State',
          field: 'state',
          width: '100px',
          filterOptions: {
            enabled: true,
            placeholder: 'All'
          }
        },
        {
          label: 'CVEs',
          field: 'cveDisplay',
          width: '150px'
        },
        {
          label: 'Installed Time',
          field: 'installed_time',
          width: '150px',
          formatFn: (val) => val || ''
        }
      ]
    }
  },
  methods: {
    getData () {
      this.loading = true
      this.patchmanager = []
      this.active = null
      this.activePatch = null
      HTTP.get(`/patchmanager/${this.activeType}/${this.id}`)
        .then((response) => {
          this.patchmanager = response.data.patchmanager
          if (this.patchmanager.length > 0) {
            this.active = this.patchmanager[0]
          }
        })
        .catch((error) => {
          this.flashMessage.error({
            message: `Failed to get Patch Manager report: ${error.message}`
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    setActive (report) {
      this.active = report
      this.activePatch = null
      this.selectedRow = -1
      this.stateFilter = null
    },
    onPatchRowClick (params) {
      this.activePatch = params.row
      this.selectedRow = params.row.originalIndex
    },
    filterByState (state) {
      this.stateFilter = state
    },
    showAllPatches () {
      this.stateFilter = null
    },
    downloadCSV () {
      this.flashMessage.info({
        message: 'Generating Patch Manager CSV report',
        time: 3000
      })
      HTTP.get(`/patchmanager/${this.activeType}/${this.id}/csv`)
        .then((response) => {
          window.location = response.data.signedUrl
          this.flashMessage.success({
            message: 'Patch Manager CSV report generation complete'
          })
        })
        .catch((error) => {
          this.flashMessage.error({
            message: `Failed to generate Patch Manager CSV report: ${error.message}`
          })
        })
    },
    complianceRowStyleFn (row) {
      if (row.compliance_status === 'NON_COMPLIANT') return 'highlight-row-critical'
      return ''
    },
    patchRowStyleFn (row) {
      if (row.originalIndex === this.selectedRow) return 'selected-row'
      if (row.state === 'Missing') return 'highlight-row-critical'
      if (row.state === 'Failed') return 'highlight-row-high'
      return ''
    },
    formatTimestamp (ts) {
      if (!ts) return ''
      const d = new Date(ts * 1000)
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString()
    }
  },
  watch: {
    id () {
      this.getData()
    }
  },
  created () {
    this.getData()
  }
}
</script>

<style scoped>
.row {
  display: flex;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
}

.empty-state {
  padding-top: 100px;
}

ul.nav a {
  color: rgba(0, 0, 0, 0.5);
}

ul.nav a:hover {
  cursor: pointer;
  color: rgba(0, 0, 0, 1);
}

.active {
  color: rgba(0, 0, 0, 0.8) !important;
}

.card {
  transition: opacity 200ms;
  background: #ffffff;
  width: auto;
  padding: 15px;
  border: none;
  border-radius: 0;
  box-sizing: content-box;
  box-shadow: 0 2px 13px -5px rgba(0,0,0,0.2);
  margin-bottom: 10px;
}

.btn-margin {
  margin-right: 5px;
}

.fixed-top-sub {
  position: sticky;
  top: 56px;
  z-index: 1000;
}

.scrollable-menu {
  height: auto;
  max-height: 80vh;
  overflow-x: scroll;
}

h4 {
  padding-bottom: 5px;
  font-size: 1.1rem;
}

h5 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-top: 8px;
}

dt {
  font-weight: 600;
  margin-bottom: 2px;
  display: inline;
  margin-right: 5px;
}

dd {
  margin-bottom: 4px;
}

.compliance-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.stat-label {
  font-weight: 600;
  font-size: 0.85rem;
  padding: 2px 8px;
  border-radius: 3px;
}

.stat-count {
  font-weight: 700;
  font-size: 1rem;
}

.stat-label.compliant {
  color: #2e7d32;
  background-color: rgba(46, 125, 50, 0.1);
}

.stat-label.non-compliant {
  color: #d32f2f;
  background-color: rgba(211, 47, 47, 0.1);
}

.stat-label.compliant-pct {
  color: #1565c0;
  background-color: rgba(21, 101, 192, 0.1);
}

.severity-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.platform-label {
  font-size: 0.85rem;
  font-weight: 500;
}

.severity-label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 3px;
}

.severity-count {
  font-weight: 700;
  font-size: 0.9rem;
}

.state-missing {
  color: #fff;
  background-color: #d32f2f;
}

.state-failed {
  color: #fff;
  background-color: #e65100;
}

.state-installed {
  color: #fff;
  background-color: #2e7d32;
}

.state-installedother, .state-installedpendingreboot, .state-installedrejected {
  color: #333;
  background-color: #e0e0e0;
}

.state-notapplicable {
  color: #555;
  background-color: #f5f5f5;
}

.severity-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
}

.severity-critical {
  color: #fff;
  background-color: #d32f2f;
}

.severity-important {
  color: #fff;
  background-color: #e65100;
}

.state-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
}

.state-badge.state-missing {
  color: #fff;
  background-color: #d32f2f;
}

.state-badge.state-failed {
  color: #fff;
  background-color: #e65100;
}

.state-badge.state-installed {
  color: #fff;
  background-color: #2e7d32;
}

.finding-detail dl {
  display: grid;
  grid-template-columns: max-content auto;
  margin: 0;
  gap: 2px 10px;
}

.finding-detail dt {
  grid-column-start: 1;
  display: block;
}

.finding-detail dd {
  grid-column-start: 2;
  display: block;
}

.detail-section {
  margin-top: 10px;
  padding-top: 6px;
  border-top: 1px solid #eee;
}

.cve-item {
  font-size: 0.85rem;
  padding: 2px 0;
}

.cve-item a {
  color: #1565c0;
  text-decoration: none;
}

.cve-item a:hover {
  text-decoration: underline;
}

.highlight-row-critical {
  background-color: rgba(211, 47, 47, 0.08) !important;
}

.highlight-row-high {
  background-color: rgba(230, 81, 0, 0.06) !important;
}

.selected-row {
  background-color: rgba(112, 161, 240, 0.15) !important;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 200ms ease-in-out;
}
.fade-enter-to {
  opacity: 1;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/PatchManager.vue